import { Link } from "react-router-dom"
import type { Case, CaseLine } from "../../api/types.ts"
import { useT } from "../../i18n/index.tsx"
import { carrierLabel } from "../../lib/carriers.ts"
import { formatTracking } from "../../lib/tracking.ts"
import { SUMMARY_OWNER } from "../../lib/case-owner.ts"
import { Card, CardHeader, DefinitionList } from "../../ui/index.ts"
import { OwnerTag } from "../badges.tsx"
import { formatDateTime, formatJpy, formatWeight, nullable } from "../format.ts"

/**
 * 읽기만 하는 요약. 고치는 일은 전부 "지금 할 일" 카드로 옮겼다 —
 * 이것이 정보와 조작을 가르는 방식이다.
 *
 * 구획마다 **그 값을 적는 쪽**을 이름표로 붙인다(`lib/case-owner.ts`). 읽기 화면이지만
 * 두 회사에게는 "이 칸을 누가 적었나" 가 곧 책임 소재다. 보는 사람의 역할과 무관하게 늘 붙인다 —
 * 남의 구획인 줄 알아야 "왜 내가 못 고치지" 를 묻지 않는다.
 */
export function SummaryTab({ item, lines }: { item: Case; lines: CaseLine[] }) {
  const t = useT()

  return (
    <>
      <Card>
        <CardHeader
          title={t("caseDetail.intake")}
          icon="inbox"
          actions={<OwnerTag owner={SUMMARY_OWNER.intake} />}
        />
        <DefinitionList
          rows={[
            { label: t("case.receivedOn"), value: item.receivedOn },
            { label: t("case.orderNo"), value: item.orderNo },
            { label: t("case.carrier"), value: nullable(carrierLabel(item.carrier, t)) },
            { label: t("case.trackingNo"), value: nullable(formatTracking(item.trackingNo)) },
            { label: t("case.customerName"), value: item.customerName },
            { label: t("case.customerPhone"), value: nullable(item.customerPhone) },
            {
              label: t("case.returnReason"),
              value: item.returnReason ? t(`reason.${item.returnReason}`) : "—",
            },
            { label: t("case.note"), value: nullable(item.note), wide: true },
          ]}
        />
      </Card>

      <Card padded={lines.length === 0}>
        <CardHeader title={t("lines.title")} icon="tag" />
        {lines.length === 0 ? (
          <p className="muted">{t("lines.empty")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                {/* 한 표에 두 쪽 숫자가 선다 — 접수수량은 화주가 적고, 검수수량은 쿨트랙JP가 센다.
                    그래서 카드가 아니라 칸 머리글마다 담당을 붙인다. */}
                <tr>
                  <th scope="col">{t("lines.item")}</th>
                  <th scope="col" className="num">
                    <span className="th-owner">
                      {t("lines.qty")}
                      <OwnerTag owner="shipper" />
                    </span>
                  </th>
                  <th scope="col" className="num">
                    <span className="th-owner">
                      {t("lines.receivedQty")}
                      <OwnerTag owner="warehouse" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  // 센 수량이 등록과 다르면 눈에 띄게 둔다 — 두 회사가 같은 화면에서 본다.
                  const mismatch = line.receivedQty !== null && line.receivedQty !== line.qty
                  return (
                    <tr key={line.id}>
                      <td>{line.description}</td>
                      <td className="num">{line.qty}</td>
                      <td className={mismatch ? "num is-mismatch" : "num"}>
                        {line.receivedQty ?? "—"}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title={t("caseDetail.arrival")}
          icon="weight"
          actions={<OwnerTag owner={SUMMARY_OWNER.arrival} />}
        />
        <DefinitionList
          rows={[
            { label: t("case.arrivedOn"), value: nullable(item.arrivedOn) },
            { label: t("case.weight"), value: formatWeight(item.weightG) },
            {
              label: t("case.freight"),
              value: item.freightType ? t(`freight.${item.freightType}`) : "—",
            },
            { label: t("case.codFee"), value: formatJpy(item.codFeeJpy) },
            {
              label: t("caseDetail.inspectedAt"),
              value: item.inspectedAt ? formatDateTime(item.inspectedAt) : "—",
            },
            { label: t("caseDetail.inspectNote"), value: nullable(item.inspectNote), wide: true },
          ]}
        />
      </Card>

      <Card>
        <CardHeader
          title={t("caseDetail.handling")}
          icon="doc"
          actions={<OwnerTag owner={SUMMARY_OWNER.handling} />}
        />
        <DefinitionList
          rows={[
            {
              label: t("case.disposition"),
              value: item.disposition ? t(`disposition.${item.disposition}`) : "—",
            },
            {
              label: t("caseDetail.confirmArrival"),
              value: item.shipperConfirmedAt ? formatDateTime(item.shipperConfirmedAt) : "—",
            },
            {
              label: t("caseDetail.confirmDoc"),
              value: item.warehouseConfirmedAt ? formatDateTime(item.warehouseConfirmedAt) : "—",
            },
            { label: t("case.closedOn"), value: nullable(item.closedOn) },
          ]}
        />
      </Card>

      <Card>
        <CardHeader
          title={t("caseDetail.shipment")}
          icon="ship"
          actions={<OwnerTag owner={SUMMARY_OWNER.shipment} />}
        />
        {item.shipmentId ? (
          <Link to={`/shipments/${item.shipmentId}`}>
            {t("caseDetail.shipment")} #{item.shipmentId}
          </Link>
        ) : (
          <p className="muted">{t("caseDetail.noShipment")}</p>
        )}
      </Card>
    </>
  )
}
